import React from 'react';
import type { Icon } from '@phosphor-icons/react';

/**
 * Navigationszeilen der Immobilien-Sidebar (Werte aus dem Prototyp).
 * art="liste": Hauptnavigation – aktiv = --hover, Icon fill/--acc, sonst duotone/--ink2.
 * art="objekt": Objektnavigation – aktiv = --acc-soft, Text/Icon --acc, Zähler-Badge.
 */
export interface NavZeileProps {
  label: string;
  icon: Icon;
  aktiv: boolean;
  onClick: () => void;
  art: 'liste' | 'objekt';
  /** Zahl rechts (Objektmodus) */
  anzahl?: number | null;
  /** Zusätzliche Markierung rechts (z. B. Badges der Hauptnavigation) */
  rechts?: React.ReactNode;
  /** Noch ohne Funktion: ausgegraut, mit Hinweis im Tooltip */
  gesperrt?: string;
  /** Mindesthöhe (mobil 44 px Hit-Target) */
  minHoehe?: number;
}

export function NavZeile({ label, icon: IconKomp, aktiv, onClick, art, anzahl, rechts, gesperrt, minHoehe }: NavZeileProps) {
  const objekt = art === 'objekt';
  const hintergrund = aktiv ? (objekt ? 'var(--acc-soft)' : 'var(--hover)') : 'transparent';
  const text = objekt ? (aktiv ? 'var(--acc)' : 'var(--ink)') : 'var(--ink)';
  const iconFarbe = aktiv ? 'var(--acc)' : 'var(--ink2)';
  const gewicht = objekt ? (aktiv ? 700 : 500) : (aktiv ? 600 : 400);
  return (
    <button
      type="button"
      onClick={gesperrt ? undefined : onClick}
      aria-current={aktiv ? 'page' : undefined}
      aria-disabled={gesperrt ? true : undefined}
      title={gesperrt}
      className={`${gesperrt ? 'cursor-not-allowed' : 'immo-hover-flaeche cursor-pointer'} flex items-center w-full text-left`}
      style={{
        gap: 10, padding: '7px 9px', minHeight: minHoehe, borderRadius: objekt ? 11 : 10, border: 'none', fontSize: 13, fontWeight: gewicht,
        background: hintergrund, color: text, transition: 'background .2s', opacity: gesperrt ? 0.45 : 1,
      }}
    >
      <span className="flex items-center justify-center shrink-0" style={{ width: 24, height: 24 }}>
        <IconKomp weight={aktiv ? 'fill' : objekt ? 'regular' : 'duotone'} size={objekt ? 18 : 19} color={iconFarbe} />
      </span>
      <span className="flex-1 min-w-0 truncate">{label}</span>
      {rechts}
      {anzahl != null && (
        <span
          className="immo-zahl flex items-center justify-center"
          style={{
            minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, fontSize: 11, fontWeight: 700,
            background: aktiv ? 'var(--acc)' : 'var(--fill2)',
            color: aktiv ? 'var(--acc-ink)' : 'var(--ink2)',
          }}
        >
          {anzahl}
        </span>
      )}
    </button>
  );
}

/** Unterpunkt (Einheit / Haus) mit Statuspunkt und Betrag. */
export function UnterZeile({ label, punkt, rechts, aktiv, onClick }: {
  label: string;
  punkt: string;
  rechts: string;
  aktiv: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={aktiv ? 'page' : undefined}
      className="immo-hover-flaeche flex items-center cursor-pointer text-left"
      style={{
        gap: 8, marginLeft: 21, padding: '5px 9px 5px 12px', border: 'none', borderLeft: `2px solid ${aktiv ? 'var(--acc)' : 'var(--line)'}`,
        borderRadius: '0 9px 9px 0', fontSize: 12, fontWeight: aktiv ? 700 : 500, color: aktiv ? 'var(--acc)' : 'var(--ink2)',
        background: aktiv ? 'var(--acc-soft)' : 'transparent',
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: punkt }} />
      <span className="flex-1 min-w-0 truncate">{label}</span>
      <span className="immo-zahl" style={{ fontSize: 11, color: 'var(--ink3)' }}>{rechts}</span>
    </button>
  );
}

/** Kleines Zähler-Badge für die Hauptnavigation (Rechnungen fällig, ungelesene E-Mails …). */
export function ZaehlBadge({ anzahl, ton, titel }: { anzahl: number; ton: 'bad' | 'info' | 'acc'; titel: string }) {
  return (
    <span
      title={titel}
      className="immo-zahl flex items-center justify-center"
      style={{ minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: `var(--${ton}-soft)`, color: `var(--${ton})` }}
    >
      {anzahl > 99 ? '99+' : anzahl}
    </span>
  );
}
